import axios from "axios";

// ============================================================
// API base URL resolver
//
// React bakes process.env into the bundle at build time, so the value we ship
// is whatever was in /app/frontend/.env when `yarn build` ran. If the bundle
// is later served from a different origin (production deployment using a
// fresh domain while the .env still points to preview), API calls go to the
// preview origin while the user's browser is on the production origin. This
// makes auth cookies cross-site and the browser silently drops them.
//
// Strategy: prefer REACT_APP_BACKEND_URL when it matches the current origin
// (dev/preview both match). Otherwise fall back to a same-origin relative
// `/api` — Emergent's K8s ingress routes `/api/*` to the backend pod, so
// cookies stay first-party and auth works.
// ============================================================
function resolveApiBase() {
  const envUrl = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") {
    return envUrl ? `${envUrl}/api` : "/api";
  }
  if (!envUrl) return "/api";
  try {
    if (new URL(envUrl).origin === window.location.origin) {
      return `${envUrl}/api`;
    }
  } catch (e) {
    // Malformed REACT_APP_BACKEND_URL — fall through to relative `/api`.
    // Runs once at module load before React mounts, so no UI to surface to.
    // eslint-disable-next-line no-console
    console.warn("[api] REACT_APP_BACKEND_URL is malformed, falling back to /api:", e?.message || e);
  }
  return "/api";
}

export const API_BASE = resolveApiBase();

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// ============================================================
// Auto-refresh access token on 401.
// Access JWT expires after 1 day; refresh JWT lives for 7. Without this,
// long-lived sessions end up showing "Not authenticated" toasts and empty
// dropdowns once the access cookie ages out. We swap it transparently and
// replay the original request once. Concurrent 401s are queued so we only
// hit /auth/refresh once per stale-token wave.
// ============================================================
const SKIP_REFRESH_PATHS = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/logout"];
let isRefreshing = false;
let waiters = [];

function shouldSkipRefresh(url) {
  if (!url) return false;
  return SKIP_REFRESH_PATHS.some((p) => url.includes(p));
}

api.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;

    if (status !== 401 || original._retry || shouldSkipRefresh(original.url)) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // Queue the request — once the in-flight refresh resolves we replay.
      return new Promise((resolve, reject) => {
        waiters.push({ resolve, reject, original });
      });
    }

    original._retry = true;
    isRefreshing = true;
    try {
      await api.post("/auth/refresh");
      // Drain the queue.
      const queued = waiters;
      waiters = [];
      queued.forEach(({ resolve, original: cfg }) => {
        cfg._retry = true;
        resolve(api(cfg));
      });
      return api(original);
    } catch (refreshErr) {
      const queued = waiters;
      waiters = [];
      queued.forEach(({ reject }) => reject(refreshErr));
      // Refresh-fail redirect to /login — but ONLY when the user is currently
      // on a route that actually requires auth. Public routes (landing, clubs,
      // facilities, matches directory, tournament directory, developer portal)
      // must keep rendering so anonymous visitors (and crawlers) see the page.
      const wasAuthProbe = (original.url || "").includes("/auth/me") || (original.url || "").includes("/me/");
      const pathname = typeof window !== "undefined" ? window.location.pathname : "";
      // Prefixes listed in App.js as <ProtectedRoute> or role-gated.
      const PROTECTED_PREFIXES = [
        "/dashboard", "/profile", "/settings", "/calendar", "/invoices",
        "/notifications", "/coach", "/club", "/admin", "/payments/success",
        "/me/passport",
      ];
      const onProtectedRoute = PROTECTED_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
      if (!wasAuthProbe && onProtectedRoute && !pathname.startsWith("/login")) {
        try {
          sessionStorage.setItem("post_login_redirect", window.location.pathname + window.location.search);
        } catch (storageErr) {
          // Private/incognito modes throw QuotaExceededError on setItem — there's
          // no recovery, the user just lands on /login without auto-return.
          // eslint-disable-next-line no-console
          console.warn("[api] post_login_redirect storage failed:", storageErr?.name || storageErr);
        }
        window.location.assign("/login");
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
